import type { ReactElement } from 'react';

import { memo, useMemo } from 'react';

import {
  ACCOUNT_MENU_URLS,
  extractAccountLinksFromNavigation,
  getAccountMenuContent,
} from './accountMenuData';
import { AccountMenuDropdown } from './AccountMenuDropdown';
import { S } from './accountMenuStyles';
import { AccountMenuTrigger } from './AccountMenuTrigger';
import { useAccountMenuInteraction } from './useAccountMenuInteraction';
import { useUserContext } from '~/utilities/context/dynamic/UserContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';

type AccountMenuProps = {
  className?: string;
};

const AccountMenuComponent = ({ className }: AccountMenuProps): ReactElement => {
  const { language } = useAppContext();
  const { loggedInRender } = useUserContext();
  const { leftNavigation } = useStaticContext();
  const interaction = useAccountMenuInteraction();

  const menuData = useMemo(() => getAccountMenuContent(language), [language]);
  const dynamicAccountData = useMemo(
    () => extractAccountLinksFromNavigation(leftNavigation),
    [leftNavigation]
  );

  const activeLinks = loggedInRender ? dynamicAccountData?.links || [] : menuData.logInLinksText;
  const menuTitle = dynamicAccountData?.title;
  const iconHref = loggedInRender ? ACCOUNT_MENU_URLS.account : ACCOUNT_MENU_URLS.logIn;

  return (
    <S.AccountMenuWrapper
      ref={interaction.menuRef}
      onMouseEnter={interaction.handleMouseEnter}
      onMouseMove={interaction.handleMouseMove}
      onMouseLeave={interaction.handleMouseLeave}
      onFocus={interaction.handleContainerFocus}
      onMouseDown={interaction.handleContainerMouseDown}
      onKeyDown={interaction.handleContainerKeyDown}
      onBlur={interaction.handleContainerBlur}
      data-testid="desktop-account-menu"
      className={className}
    >
      <AccountMenuTrigger
        href={iconHref}
        iconAriaLabel={menuData.a11y.myAccountIcon}
        menuAriaLabel={menuData.a11y.myAccountMenu}
        isOpen={interaction.isOpen}
        iconLinkRef={interaction.iconLinkRef}
        toggleButtonRef={interaction.toggleButtonRef}
        onLinkFocus={interaction.handleLinkFocus}
        onLinkKeyDown={interaction.handleLinkKeyDown}
        onToggleClick={interaction.handleToggleClick}
        onToggleKeyDown={interaction.handleToggleKeyDown}
        onIconMouseEnter={interaction.handleIconMouseEnter}
        onIconMouseMove={interaction.handleIconMouseMove}
      />

      <AccountMenuDropdown
        activeLinks={activeLinks}
        menuTitle={menuTitle}
        isOpen={interaction.isOpen}
        expandedItems={interaction.expandedItems}
        onToggleItem={interaction.toggleItemExpanded}
        onClose={interaction.closeMenu}
        onBlur={interaction.handleDropdownBlur}
      />
    </S.AccountMenuWrapper>
  );
};

export const AccountMenu = memo(AccountMenuComponent);
